import React, { Component } from 'react';
import { connect } from 'react-redux';
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import HeatmapModule from 'highcharts/modules/heatmap';
import TreemapModule from 'highcharts/modules/treemap';
import Exporting from 'highcharts/modules/exporting';
import * as healthDataReducer from '../../health-data/reducer';
import CircularProgress from '@material-ui/core/CircularProgress';

// Load Highcharts modules
HeatmapModule(Highcharts);
TreemapModule(Highcharts);
Exporting(Highcharts);

class HeatMap extends Component {
  constructor(props) {
    super(props);
    this.state = {
      loading: true,
      isFullscreen: false,
      isAnimating: false,
    };
    this.chartRef = React.createRef();
    this.animationTimeouts = [];
  }

  componentDidMount() {
    if (this.props.heatMapData && Object.keys(this.props.heatMapData).length > 0) {
      this.setState({ loading: false });
    }
    document.addEventListener('fullscreenchange', this.handleFullscreenChange);
    window.addEventListener('resize', this.handleResize);
  }

  componentWillUnmount() {
    document.removeEventListener('fullscreenchange', this.handleFullscreenChange);
    window.removeEventListener('resize', this.handleResize);
    this.animationTimeouts.forEach(timeout => clearTimeout(timeout));
    this.animationTimeouts = [];
  }

  componentDidUpdate(prevProps) {
    if (
      Object.keys(prevProps.heatMapData || {}).length === 0 &&
      Object.keys(this.props.heatMapData || {}).length > 0
    ) {
      this.setState({ loading: false });
    }
  }

  handleResize = () => {
    if (this.state.isFullscreen && this.chartRef.current) {
      const chart = this.chartRef.current.chart;
      if (chart) {
        // Reflow chart when window is resized in fullscreen
        chart.reflow();
      }
    }
  };

  handleFullscreenChange = () => {
    const isFullscreen = !!document.fullscreenElement;
    
    // Start animation
    this.setState({ isAnimating: true, isFullscreen });
    
    // Clear any existing timeouts
    this.animationTimeouts.forEach(timeout => clearTimeout(timeout));
    this.animationTimeouts = [];
    
    // Update chart with animation after a brief delay to allow CSS transition to start
    const chartUpdateTimeout = setTimeout(() => {
      if (this.chartRef.current) {
        const chart = this.chartRef.current.chart;
        
        // Calculate height for fullscreen (use full viewport height)
        const chartHeight = isFullscreen ? window.innerHeight : 403;
        
        chart.update({
          chart: {
            height: chartHeight,
          },
          plotOptions: {
            treemap: {
              dataLabels: {
                formatter: this.getDataLabelFormatter(isFullscreen),
              },
            },
          },
          exporting: {
            buttons: {
              contextButton: {
                menuItems: [
                  {
                    textKey: 'viewFullscreen',
                    text: isFullscreen ? 'Exit from Full Screen' : 'View in Full Screen',
                    onclick: function () {
                      this.fullscreen.toggle();
                    },
                  },
                ],
              },
            },
          },
          animation: {
            duration: 500,
          },
        }, true); // true enables animation
        
        // Reflow chart to ensure proper sizing after a short delay
        const reflowTimeout = setTimeout(() => {
          if (chart) {
            chart.reflow();
          }
        }, 150);
        this.animationTimeouts.push(reflowTimeout);
      }
      
      // End animation after transition completes (700ms to match transform duration)
      const animationEndTimeout = setTimeout(() => {
        this.setState({ isAnimating: false });
      }, 700);
      this.animationTimeouts.push(animationEndTimeout);
    }, 50);
    this.animationTimeouts.push(chartUpdateTimeout);
  };

  getDataLabelFormatter = (isFullscreen) => {
    const heatMapData = this.props.heatMapData;
    return function () {
      const desc = heatMapData[this.point.id]?.description ?? '';
      const pr = this.point.colorValue?.toFixed(1) ?? '--';

      if (isFullscreen) {
        return `<div style="text-align:center;">
                  <div style="font-weight:bold">${this.point.id}</div>
                  <div>${desc}</div>
                  <div>${pr}%</div>
                </div>`;
      } else {
        return `<div style="text-align:center;">
                  <div style="font-weight:bold">${this.point.id}</div>
                  <div>${pr}%</div>
                </div>`;
      }
    };
  };

  render() {
    const { heatMapData } = this.props;
    const { loading, isFullscreen, isAnimating } = this.state;

    if (loading) {
      return (
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '200px' }}>
          <CircularProgress />
        </div>
      );
    }
    if (!heatMapData || Object.keys(heatMapData).length === 0) {
      return <div>No heat map data available.</div>;
    }

    const data = Object.entries(heatMapData).map(([stationId, { prData, systemCapacity }]) => ({
      id: stationId,
      name: `${stationId}<br/>${prData.toFixed(1)}%`,
      value: systemCapacity,
      colorValue: prData,
    }));

    const stationCount = Object.keys(heatMapData).length;
    const dynamicFontSize = (() => {
      if (isFullscreen) {
        return stationCount > 50 ? '14px' :
              stationCount > 20 ? '17px' :
              stationCount > 10 ? '18px' :
              stationCount > 5  ? '19px' : '21px';
      } else {
        return stationCount > 50 ? '11px' :
              stationCount > 20 ? '14px' :
              stationCount > 10 ? '15px' :
              stationCount > 5  ? '16px' : '18px';
      }
    })();

    const dynamicFontSizeToolTip = (() => {
      if (isFullscreen) {
        return stationCount > 50 ? '17px' :
              stationCount > 20 ? '19px' :
              stationCount > 10 ? '20px' :
              stationCount > 5  ? '21px' : '23px';
      } else {
        return stationCount > 50 ? '15px' :
              stationCount > 20 ? '17px' :
              stationCount > 10 ? '18px' :
              stationCount > 5  ? '19px' : '21px';
      }
    })();


    // Calculate chart height based on fullscreen state
    const chartHeight = isFullscreen ? window.innerHeight : 403;

    const options = {
      chart: {
        type: 'treemap',
        height: chartHeight,
        style: {
          borderRadius: '4px',
          overflow: 'hidden',
        },
        animation: {
          duration: 500,
        },
        events: {
          render: function () {
            if (this.series && this.series[0] && this.series[0].points) {
              this.series[0].points.forEach(point => {
                if (point.graphic && point.graphic.element) {
                  point.graphic.element.setAttribute('rx', 5);
                  point.graphic.element.setAttribute('ry', 5);
                }
              });
            }
          },
        },
      },
      title: { text: '' },
      colorAxis: {
        min: 0,
        max: 100,
        stops: [
          [0.0, '#FB4141'],
          [0.2, '#FF9B2F'],
          [0.5, '#FFA239'],
          [0.70, '#FFE700'],
          [1.0, '#00C853'],
        ],
        labels: {
          format: '{value}%',
        },
      },
      legend: {
        layout: 'horizontal',
        align: 'center',
        verticalAlign: 'bottom',
        symbolWidth: 300,
        symbolHeight: 5,
        itemMarginTop: 0,
        itemStyle: {
          fontWeight: 'bold',
          fontSize: '13px',
        },
      },
      tooltip: {
        useHTML: true,
        formatter: function () {
          const cap = heatMapData[this.point.id]?.systemCapacity ?? '--';
          const pr = heatMapData[this.point.id]?.prData ?? '--';
          const desc = heatMapData[this.point.id]?.description ?? '--';
          const bgColor = this.point.color;

          return `
            <div style="
              background: ${bgColor}; 
              padding: 10px; 
              border-radius: 5px;
              box-shadow: 0 2px 8px rgba(0,0,0,0.3);
              min-width: 150px;
            ">
              <div style="font-size: ${dynamicFontSizeToolTip}; font-weight: bold;"> ${this.point.id}</div>
              <div style="font-size: ${dynamicFontSizeToolTip}; font-weight: bold;"> ${desc} </div>
              <div style="font-size: ${dynamicFontSizeToolTip};">Capacity: <b> ${cap} kWp</b></div>
              <div style="font-size: ${dynamicFontSizeToolTip};">PR: <b>${pr}%</b></div>
            </div>
          `;
        },
      },
      plotOptions: {
        treemap: {
          layoutAlgorithm: 'squarified',
          colorByPoint: false,
          dataLabels: {
            enabled: true,
            useHTML: true,
            align: 'center',
            verticalAlign: 'middle',
            overflow: 'none',
            style: {
              textOutline: 'none',
              fontSize: dynamicFontSize,
              overflow: 'hidden',
              whiteSpace: 'pre-wrap',
              textOverflow: 'ellipsis',
            },
            formatter: this.getDataLabelFormatter(isFullscreen),
          },
        },
      },
      series: [
        {
          type: 'treemap',
          layoutAlgorithm: 'squarified',
          data,
        },
      ],
      credits: { enabled: false },
      exporting: {
        enabled: true,
        buttons: {
          contextButton: {
            menuItems: [
              {
                textKey: 'viewFullscreen',
                text: isFullscreen ? 'Exit Full Screen' : 'View in Full Screen',
                onclick: function () {
                  this.fullscreen.toggle();
                },
              },
            ],
          },
        },
      },
    };

    // Different animation effects for entering vs exiting fullscreen
    // When entering: more dramatic scale (0.9) for elastic effect
    // When exiting: less dramatic scale (0.98) to minimize shrinking
    const scaleValue = isAnimating 
      ? (isFullscreen ? 'scale(0.9)' : 'scale(0.98)')  // Enter: 0.9, Exit: 0.98
      : 'scale(1)';
    
    const containerStyle = {
      transition: 'transform 700ms cubic-bezier(0.175, 0.885, 0.32, 0.999), opacity 500ms ease-in-out',
      transform: scaleValue,
      opacity: isAnimating ? 0.8 : 1,
      width: '100%',
      height: '100%',
    };

    return (
      <div style={containerStyle}>
        <HighchartsReact highcharts={Highcharts} options={options} ref={this.chartRef} />
      </div>
    );
  }
}

const mapStateToProps = (state) => ({
  heatMapData: healthDataReducer.getLatestPR(state),
});

export default connect(mapStateToProps)(HeatMap);
